import { intl, ui } from '@beelday/common'
import { UUID } from 'common/types'
import { useKnowledgeCheckResults } from 'features/knowledge-checks/use-knowledge-check-results'
import { useMemo, useState } from 'react'
import { answeredQuestions } from './model/knowledge-check-outcome'
import {
	KnowledgeCheckAnswers,
	KnowledgeCheckSummary,
} from './presentation/knowledge-check-outcome-view'
import { Confirmation, StateActions } from './presentation/primitives'

type Props = {
	knowledgeCheckId: UUID
	useCorrectAnswers: boolean
}

/**
 * After the last question: the score (for a check with correct answers) and
 * the participant's own answers behind "See your answers". Until the results
 * arrive the phone says the answers are sent.
 */
export const ConfiguredKnowledgeCheckOutcome = ({
	knowledgeCheckId,
	useCorrectAnswers,
}: Props): JSX.Element => {
	const { results, error, reload } = useKnowledgeCheckResults(knowledgeCheckId)
	const [showAnswers, setShowAnswers] = useState(false)
	const questions = useMemo(
		() => (results ? answeredQuestions(results, useCorrectAnswers) : []),
		[results, useCorrectAnswers]
	)

	if (!results) {
		return (
			<Confirmation
				title={
					<intl.Translate>in_person.mobile.sent.answers_title</intl.Translate>
				}
				description={
					<intl.Translate>
						in_person.mobile.knowledge_check.summary.description
					</intl.Translate>
				}
				testId="in_person.mobile.knowledge_check.sent"
			>
				{error ? (
					<StateActions>
						<ui.LinkButton type="button" onClick={reload}>
							<intl.Translate>in_person.mobile.retry</intl.Translate>
						</ui.LinkButton>
					</StateActions>
				) : null}
			</Confirmation>
		)
	}

	if (showAnswers) {
		return (
			<KnowledgeCheckAnswers
				questions={questions}
				scored={useCorrectAnswers}
				onBack={() => setShowAnswers(false)}
			/>
		)
	}

	return (
		<KnowledgeCheckSummary
			score={
				useCorrectAnswers
					? {
							correct: results.scoreDto.correctAnswers,
							total: results.scoreDto.totalQuestions,
					  }
					: undefined
			}
			onSeeAnswers={() => setShowAnswers(true)}
		/>
	)
}

export default ConfiguredKnowledgeCheckOutcome
